/**
 * Permission-based gates for UI (never role names).
 *
 * Strings MUST match RolePermission rows in TraceOS (see seed/migrations).
 * There is no `proposals.delete` in the catalog — archive/remove flows use
 * `proposals.manage`, `rfp.proposals.manage`, or broad `proposals.write`.
 */

export type HasAnyPermissionFn = (names: string[]) => boolean;

/** Start a new proposal / proposal-generation job */
export function canCreateProposal(hasAnyPermission: HasAnyPermissionFn): boolean {
  return hasAnyPermission([
    'proposals.create',
    'rfp.proposals.create',
    'proposals.write',
    'proposals.manage',
    'rfp.proposals.manage',
  ]);
}

/** Edit proposal content, workflow, citations, staff, etc. */
export function canEditProposal(hasAnyPermission: HasAnyPermissionFn): boolean {
  return hasAnyPermission([
    'proposals.edit',
    'rfp.proposals.edit',
    'proposals.write',
    'proposals.manage',
    'rfp.proposals.manage',
  ]);
}

/**
 * Archive proposal, cancel jobs, or other lifecycle/removal actions.
 * There is no `proposals.delete` in the DB catalog — use manage / broad write.
 */
export function canArchiveProposal(hasAnyPermission: HasAnyPermissionFn): boolean {
  return hasAnyPermission([
    'proposals.manage',
    'rfp.proposals.manage',
    'proposals.write',
  ]);
}

/** @deprecated Use canArchiveProposal — name referenced legacy UI copy ("Delete"); permissions are manage/write. */
export const canDeleteProposal = canArchiveProposal;

/** Mutate opportunities (tracking, match scores, lists, ranking jobs, etc.) */
export function canWriteOpportunities(hasAnyPermission: HasAnyPermissionFn): boolean {
  return hasAnyPermission(['opportunities.manage', 'opportunities.write']);
}

/** SPARC-002: Kanban pipeline stage drag-and-drop (admin role required) */
export function canAdminOpportunityPipeline(hasAnyPermission: HasAnyPermissionFn): boolean {
  return hasAnyPermission([
    'admin',
    'admin_tech',
    'opportunities.manage',
    'keystone_rfp_admin',
    'keystone_rfp_admin_tech',
  ]);
}

/** Upload / replace / remove documents in project context */
export function canWriteDocuments(hasAnyPermission: HasAnyPermissionFn): boolean {
  return hasAnyPermission(['documents.write', 'documents.manage']);
}

/** Create or change evidence / citations (platform-level) */
export function canWriteEvidence(hasAnyPermission: HasAnyPermissionFn): boolean {
  return hasAnyPermission(['evidence.write', 'evidence.create', 'evidence.manage']);
}

/** Manage users (edit users, roles, password reset) */
export function canManageUsers(hasAnyPermission: HasAnyPermissionFn): boolean {
  return hasAnyPermission([
    'users.manage',
    'users.write',
    'roles.manage',
    'admin',
    'admin_tech',
    'keystone_rfp_admin',
    'keystone_rfp_admin_tech',
  ]);
}
